import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Lock } from 'lucide-react';
import type { LearnPostSummary } from '@/api/types';
import { topicBySlug } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { learnPath } from '@/lib/structuredData';
import { formatPostDate } from './learnFormat';
import { localizedSummary } from './learnLocale';

/** /learn ro'yxati, "Shu mavzuda yana" va mavzu sahifasidagi darslik kartochkasi. */
export function LearnPostCard({
  post,
  compact = false,
  hideTopic = false,
}: {
  post: LearnPostSummary;
  compact?: boolean;
  hideTopic?: boolean;
}) {
  const { t } = useTranslation();
  const { l, lang } = useLocalized();
  const topic = post.topic ? topicBySlug(post.topic) : undefined;
  const { title, summary, translated } = localizedSummary(post, lang);

  return (
    <Link to={learnPath(post.slug)} className="card card-hover group flex h-full flex-col p-5">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-content-muted">
        {topic && !hideTopic && (
          <span className="tag gap-1">
            <topic.icon className="h-3.5 w-3.5" aria-hidden />
            {l(topic.title)}
          </span>
        )}
        {post.level && <span className="tag">{t(`courses.level.${post.level}`)}</span>}
        {!post.free && (
          <span className="inline-flex items-center gap-1 text-[var(--medium)]">
            <Lock className="h-3 w-3" /> Premium
          </span>
        )}
        {!translated && <span className="tag">{t('learn.uzOnlyBadge')}</span>}
      </div>
      <h3
        lang={translated ? undefined : 'uz'}
        className="text-base font-semibold leading-snug transition-colors group-hover:text-brand-600"
      >
        {title}
      </h3>
      {!compact && (
        <p lang={translated ? undefined : 'uz'} className="mt-2 line-clamp-3 text-sm text-content-secondary">
          {summary}
        </p>
      )}
      <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-content-muted">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {t('learn.readingTime', { count: post.readingMinutes })}
        </span>
        {post.publishedAt && <span>{formatPostDate(post.publishedAt, lang)}</span>}
      </div>
    </Link>
  );
}
